import { useEffect, useState } from "react";
import { BookOpen, FileText, Layers, Pencil, Plus, RefreshCw, Save, Star, Trash2 } from "lucide-react";

import { useStore } from "../context/StoreContext";
import DocEditor from "../components/DocEditor";
import SegmentEditor from "../components/SegmentEditor";
import { PageHeader } from "../components/bits";
import { EmptyHint, GhostBtn, IconBtn, PrimaryBtn, Spinner } from "../components/ui";

export default function DocumentsPage() {
  const {
    documents,
    saveDocument,
    deleteDocument,
    toggleDocUpdate,
    settings,
    generateThemes,
    saveThemes,
    themesBusy,
    showToast,
  } = useStore();
  const [editDoc, setEditDoc] = useState(null);
  const [themesLocal, setThemesLocal] = useState(settings.themes || []);
  const [savingThemes, setSavingThemes] = useState(false);

  useEffect(() => setThemesLocal(settings.themes || []), [settings.themes]);

  const themesDirty = JSON.stringify(themesLocal) !== JSON.stringify(settings.themes || []);

  const saveThemesNow = async () => {
    setSavingThemes(true);
    try {
      await saveThemes(themesLocal);
    } catch {
      showToast("Could not save themes");
    }
    setSavingThemes(false);
  };

  const addNew = () =>
    setEditDoc({ title: "", type: "Feature", is_update: false, content: "", _isNew: true });

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Knowledge base"
        subtitle="Everything remifo writes from. Keep these current and your calendar stays accurate."
        right={
          <PrimaryBtn onClick={addNew}>
            <Plus size={16} />
            Add document
          </PrimaryBtn>
        }
      />

      {documents.length === 0 ? (
        <EmptyHint
          icon={<BookOpen size={28} />}
          title="No documents yet"
          body="Add product docs, feature specs, FAQs, or changelogs so remifo has something to write from."
          action={
            <PrimaryBtn onClick={addNew}>
              <Plus size={16} />
              Add document
            </PrimaryBtn>
          }
        />
      ) : (
        <div className="grid sm:grid-cols-2 gap-3.5">
          {documents.map((d) => (
            <div key={d.id} className="bg-surface border border-border rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-foreground/5 flex items-center justify-center shrink-0 text-muted">
                  <FileText size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-foreground text-[15px] truncate">{d.title}</h3>
                    {d.is_update ? (
                      <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-foreground/10 text-foreground">
                        NEW
                      </span>
                    ) : null}
                  </div>
                  <div className="text-[12px] text-faint mt-0.5">
                    {d.type} · edited {new Date(d.updated_at).toLocaleDateString()}
                  </div>
                  <p className="text-[13px] text-muted mt-2 line-clamp-3">{d.content}</p>
                </div>
              </div>
              <div className="flex items-center gap-1 mt-3 pt-3 border-t border-border">
                <button
                  onClick={() => toggleDocUpdate(d)}
                  title="Highlighted docs are featured in the next calendar so launches and announcements are not missed."
                  className={`text-[12.5px] font-medium px-2.5 py-1.5 rounded-lg hover:bg-foreground/5 flex items-center gap-1.5 transition active:scale-[0.97] ${
                    d.is_update ? "text-foreground" : "text-muted"
                  }`}
                >
                  <Star size={14} />
                  {d.is_update ? "Highlighted" : "Highlight as update"}
                </button>
                <div className="ml-auto flex">
                  <IconBtn title="Edit" onClick={() => setEditDoc(d)}>
                    <Pencil size={15} />
                  </IconBtn>
                  <IconBtn title="Delete" onClick={() => deleteDocument(d.id)}>
                    <Trash2 size={15} />
                  </IconBtn>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 bg-surface border border-border rounded-2xl p-5 space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-foreground">
              <Layers size={17} className="text-muted" />
              <div className="font-medium text-[15px]">Content themes</div>
            </div>
            <p className="text-[13px] text-muted mt-1.5 leading-relaxed max-w-xl">
              The distinct angles your content can take, derived from everything above. Generate them from your
              knowledge base, edit as you like, then focus a batch on one or more when you generate.
            </p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto sm:shrink-0 [&>*]:flex-1 sm:[&>*]:flex-none">
            <GhostBtn onClick={() => generateThemes()} disabled={themesBusy || documents.length === 0}>
              {themesBusy ? <Spinner size={15} /> : <RefreshCw size={15} />}
              {themesLocal.length ? "Regenerate" : "Generate from knowledge base"}
            </GhostBtn>
            {themesDirty ? (
              <PrimaryBtn onClick={saveThemesNow} disabled={savingThemes}>
                <Save size={16} />
                Save
              </PrimaryBtn>
            ) : null}
          </div>
        </div>

        {documents.length === 0 ? (
          <div className="text-[13.5px] text-faint">Add a document first, then generate themes from it.</div>
        ) : themesLocal.length === 0 ? (
          <div className="text-[13.5px] text-faint">
            No themes yet. Generate them from your knowledge base, or add your own.
          </div>
        ) : (
          <SegmentEditor
            items={themesLocal}
            onChange={setThemesLocal}
            labelPlaceholder="Theme name"
            descPlaceholder="What this theme covers and the angle it takes"
            addLabel="Add theme"
          />
        )}
      </div>

      <DocEditor
        open={!!editDoc}
        doc={editDoc}
        onClose={() => setEditDoc(null)}
        onSave={async (doc) => {
          await saveDocument(doc);
          setEditDoc(null);
        }}
      />
    </div>
  );
}